import React, { createContext, useContext, useEffect, useRef, ReactNode } from "react";
import { create } from "zustand";
import type { User, LoginCredentials, SignupForm } from "@shared/types/User";
import {
  getCurrentUser,
  loginUser,
  type MfaRequiredResponse,
  loginWithOAuthProvider,
  refreshUserSession,
  signupUser,
  updateUserProfile,
  logoutUser,
} from "../../../modules/auth/api/auth.api";
import { AuthState } from "@shared/types/auth.types";
import { storage } from "@/shared/utils/storage";
import { getScopeFromRole } from "@/constants/routes";
import type { AuthScope } from "@/constants/apiRoutes";
import { clearAccessToken, getAccessToken, setAccessToken } from "@/shared/utils/token";
import { clearPortalHostOverride } from "@/utils/portalHost";
export const STORAGE_KEY = "confidacare_auth";

let restoreSessionRequest: Promise<void> | null = null;
const RESTORE_TIMEOUT_MS = 5000;
const HYDRATION_GUARD_TIMEOUT_MS = 8000;

type StoredAuth = {
  user: User;
  token?: string;
  scope?: "user" | "providers" | "admins";
};

interface AuthStore extends AuthState {
  user: User | null;
  isHydrated: boolean;
  isLoading: boolean;
  token: string | null;
  isAuthenticated: boolean;
  restoreSession: () => Promise<void>;
  login: (credentials: LoginCredentials) => Promise<boolean | MfaRequiredResponse>;
  signup: (form: SignupForm) => Promise<boolean>;
  logout: () => void;
  update: (updates: Partial<User>) => Promise<void>;
  restore: () => Promise<void>;
  loginWithProvider: (
    provider: string,
    credential?: string,
  ) => Promise<boolean>;
  completeOAuthLogin: (
    token: string,
  ) => Promise<boolean>;
  completeMagicLinkLogin: (
    accessToken: string,
    user?: User | null,
    scope?: AuthScope,
  ) => Promise<boolean>;
  syncUser: (user: User) => void;
}

function persistAuthSession(input: {
  user: User;
  token: string;
  scope: "user" | "providers" | "admins";
}) {
  const shouldPersistToken = input.scope !== "user";
  setAccessToken(input.token);
  storage.set(STORAGE_KEY, {
    user: input.user,
    scope: input.scope,
    ...(shouldPersistToken ? { token: input.token } : {}),
  });
}

async function withRestoreTimeout<T>(promise: Promise<T>) {
  return await Promise.race([
    promise,
    new Promise<T>((_, reject) => {
      window.setTimeout(() => {
        reject(new Error("Session restore timed out"));
      }, RESTORE_TIMEOUT_MS);
    }),
  ]);
}

export const useAuthStore = create<AuthStore>((set, get) => ({
  user: null,
  isAuthenticated: false,
  isLoading: false,
  token: null,
  isHydrated: false,

  login: async (credentials) => {
    set({ isLoading: true });

    try {
      const data = await loginUser(credentials);
      const scope = credentials.scope ?? "user";

      if ("requiresMfa" in data && data.requiresMfa) {
        set({ isLoading: false });
        return data;
      }

      const token = data.token ?? null;

      if (!token) {
        throw new Error("Login response missing token");
      }

      const basicUser = await getCurrentUser(token, scope);

      persistAuthSession({ user: basicUser, token, scope });

      set({
        user: basicUser,
        token,
        isAuthenticated: true,
        isLoading: false,
      });

      return true;
    } catch (error) {
      console.error("login failed");

      clearAccessToken();
      storage.remove(STORAGE_KEY);
      set({
        user: null,
        token: null,
        isAuthenticated: false,
        isLoading: false,
      });

      throw error;
    }
  },

  signup: async (form) => {
    set({ isLoading: true });

    try {
      await signupUser(form);

      clearAccessToken();
      storage.remove(STORAGE_KEY);

      set({
        user: null,
        token: null,
        isAuthenticated: false,
        isLoading: false,
      });

      return true;
    } catch (error) {
      console.error("signup failed");

      clearAccessToken();
      storage.remove(STORAGE_KEY);
      set({
        user: null,
        token: null,
        isAuthenticated: false,
        isLoading: false,
      });

      throw error;
    }
  },

    logout: () => {
    logoutUser(getScopeFromRole(get().user?.role)).catch(() => {
      console.warn("backend logout failed");
    });

    clearAccessToken();
    storage.remove(STORAGE_KEY);
    clearPortalHostOverride();

    set({
      user: null,
      token: null,
      isAuthenticated: false,
      isLoading: false,
    });
  },

  update: async (updates) => {
    const current = get().user;
    const token = get().token;

    if (!current || !token) return;

    set({ isLoading: true });

    try {
      const updated = await updateUserProfile(updates);

      persistAuthSession({
        user: updated,
        token,
        scope:
          storage.get<StoredAuth>(STORAGE_KEY)?.scope ??
          getScopeFromRole(updated.role),
      });

      set({
        user: updated,
        token,
        isAuthenticated: true,
        isLoading: false,
      });
    } catch (error) {
      console.error("profile update failed");
      set({ isLoading: false });
      throw error;
    }
  },

  syncUser: (user) => {
    const token = get().token;
    if (!token) return;

    persistAuthSession({
      user,
      token,
      scope:
        storage.get<StoredAuth>(STORAGE_KEY)?.scope ?? getScopeFromRole(user.role),
    });

    set({
      user,
      token,
      isAuthenticated: true,
    });
  },

  restoreSession: async () => {
    if (restoreSessionRequest) {
      return restoreSessionRequest;
    }

    restoreSessionRequest = (async () => {
    const saved = storage.get<StoredAuth>(STORAGE_KEY);

    if (!saved) {
      clearAccessToken();
      set({
        user: null,
        token: null,
        isAuthenticated: false,
        isLoading: false,
        isHydrated: true,
      });
      return;
    }

    const scope = saved?.scope ?? getScopeFromRole(saved?.user?.role);

    if (!saved?.user && scope !== "user") {
      set({
        user: null,
        token: null,
        isAuthenticated: false,
        isLoading: false,
        isHydrated: true,
      });
      return;
    }

    set({ isLoading: true });

    try {
      if (scope === "user") {
        const existingToken = getAccessToken();

        if (existingToken) {
          const freshUser = await withRestoreTimeout(
            getCurrentUser(existingToken, scope),
          );
          persistAuthSession({
            user: freshUser,
            token: existingToken,
            scope,
          });

          set({
            user: freshUser,
            token: existingToken,
            isAuthenticated: true,
            isLoading: false,
            isHydrated: true,
          });

          return;
        }

        const refreshed = await withRestoreTimeout(refreshUserSession());
        persistAuthSession({
          user: refreshed.user,
          token: refreshed.token,
          scope,
        });

        set({
          user: refreshed.user,
          token: refreshed.token,
          isAuthenticated: true,
          isLoading: false,
          isHydrated: true,
        });

        return;
      }

      if (!saved?.token) {
        throw new Error("Missing persisted token");
      }

      setAccessToken(saved.token);
      let freshUser: User;
      try {
        freshUser = await withRestoreTimeout(
          getCurrentUser(saved.token, scope),
        );
      } catch {
        const refreshed = await withRestoreTimeout(refreshUserSession(scope));
        setAccessToken(refreshed.token);
        freshUser = refreshed.user;
        storage.set(STORAGE_KEY, {
          user: refreshed.user,
          token: refreshed.token,
          scope,
        });
      }

      persistAuthSession({
        user: freshUser,
        token: getAccessToken() ?? saved.token,
        scope,
      });

      set({
        user: freshUser,
        token: getAccessToken() ?? saved.token,
        isAuthenticated: true,
        isLoading: false,
        isHydrated: true,
      });
    } catch (error) {
      const status =
        typeof error === "object" &&
        error !== null &&
        "status" in error &&
        typeof (error as { status?: unknown }).status === "number"
          ? (error as { status: number }).status
          : undefined;

      if (status !== 401 && status !== 429) {
        console.error("restoreSession failed");
      }

      clearAccessToken();
      storage.remove(STORAGE_KEY);

      set({
        user: null,
        token: null,
        isAuthenticated: false,
        isLoading: false,
        isHydrated: true,
      });
    }
    })();

    try {
      await restoreSessionRequest;
    } finally {
      restoreSessionRequest = null;
      const state = get();
      if (!state.isHydrated) {
        set({
          isHydrated: true,
          isLoading: false,
          isAuthenticated: false,
          user: null,
          token: null,
        });
      }
    }
  },

  loginWithProvider: async (provider: string, credential?: string) => {
    set({ isLoading: true });

    try {
      const { user, token } = await loginWithOAuthProvider({
        provider,
        credential: credential || "",
      });

      persistAuthSession({ user, token, scope: "user" });

      set({
        user,
        token,
        isAuthenticated: true,
        isLoading: false,
      });

      return true;
    } catch (error) {
      console.error("provider login failed");

      clearAccessToken();
      storage.remove(STORAGE_KEY);
      set({
        user: null,
        token: null,
        isAuthenticated: false,
        isLoading: false,
      });

      return false;
    }
  },

  completeOAuthLogin: async (token: string) => {
    set({ isLoading: true });

    try {
      const user = await getCurrentUser(token, "user");

      persistAuthSession({ user, token, scope: "user" });

      set({
        user,
        token,
        isAuthenticated: true,
        isLoading: false,
      });

      return true;
    } catch (error) {
      console.error("completeOAuthLogin failed");

      clearAccessToken();
      storage.remove(STORAGE_KEY);
      set({
        user: null,
        token: null,
        isAuthenticated: false,
        isLoading: false,
      });

      return false;
    }
  },

  completeMagicLinkLogin: async (
    accessToken: string,
    user?: User | null,
    scope?: AuthScope,
  ) => {
    set({ isLoading: true });

    try {
      const inferredScope = scope ?? (user ? getScopeFromRole(user.role) : "user");
      const finalUser = user ?? (await getCurrentUser(accessToken, inferredScope));
      const finalScope = scope ?? getScopeFromRole(finalUser.role);

      persistAuthSession({ user: finalUser, token: accessToken, scope: finalScope });

      set({
        user: finalUser,
        token: accessToken,
        isAuthenticated: true,
        isLoading: false,
      });

      return true;
    } catch (error) {
      console.error("completeMagicLinkLogin failed");

      clearAccessToken();
      storage.remove(STORAGE_KEY);
      set({
        user: null,
        token: null,
        isAuthenticated: false,
        isLoading: false,
      });

      return false;
    }
  },

  restore: async () => {
    await get().restoreSession();
  },
}));

const AuthContext = createContext<AuthStore | null>(null);

export const AuthProvider: React.FC<{ children: ReactNode }> = ({
  children,
}) => {
  const store = useAuthStore();
  const hasRestoredSessionRef = useRef(false);

  useEffect(() => {
    if (hasRestoredSessionRef.current) {
      return;
    }

    hasRestoredSessionRef.current = true;
    const guardId = window.setTimeout(() => {
      const state = useAuthStore.getState();
      if (!state.isHydrated) {
        useAuthStore.setState({
          isHydrated: true,
          isLoading: false,
          isAuthenticated: false,
          user: null,
          token: null,
        });
      }
    }, HYDRATION_GUARD_TIMEOUT_MS);

    useAuthStore
      .getState()
      .restoreSession()
      .catch(() => {
        console.error("restoreSession bootstrap failed");
      })
      .finally(() => {
        window.clearTimeout(guardId);
      });
  }, []);

  return <AuthContext.Provider value={store}>{children}</AuthContext.Provider>;
};

export const useAuth = (): AuthStore => {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used inside AuthProvider");
  return ctx;
};

